import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { flatMap } from 'lodash-es';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import {
  CommercialInvitation,
  GetResponse,
  Invitation,
  InvitationAccessLevel,
  InvitationStatusType,
  InvitationTokenType,
  InvitationsApi,
  ListResponse,
  ViError,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { UIService } from '@services';
import {
  InstallationDetailsStore,
  InvitationsStore,
  isSendByBuildingManager,
  isSendByLoggedInUser,
} from '@store';

import { InvitationsMessagesService } from '../_services/invitations-messages.service';
import { AuthStore } from '../auth';
import { InstallationsStore } from '../installations';

import { InvitationsActions } from './invitations.actions';
import { BulkListInvitationsCombinedResponses } from './invitations.model';
import { InvitationsService } from 'src/app/+invitations/service/invitations.service';

@Injectable()
export class InvitationsEffects {
  private static RELOAD_INVITATIONS_DEBOUNCE = 5000;

  createInvitation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.createInvitation),
      tap(() => this.uiService.blockUI()),
      mergeMap((action) =>
        this.invitationsApi.createInvitation(action).pipe(
          map((response) => {
            return InvitationsActions.createInvitationSuccess({ response: response.data });
          }),

          catchError((error: { errorType?: string; message?: string; statusCode?: number }) => {
            this.uiService.unblockUI();
            this.toastService.error({
              message: this.translateService.instant('+INVITATIONS.CREATE_INVITATION_ERROR'),
            });

            return of(InvitationsActions.createInvitationError({ error }));
          })
        )
      )
    );
  });

  getIncomingInvitationsCount$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.getIncomingInvitationsCount),
      concatLatestFrom(() => this.authStore.getCompanyId()),
      mergeMap(([_action, companyId]) =>
        this.invitationsApi
          .listInvitations({
            limit: 100,
            status: InvitationStatusType.Pending,
          })
          .pipe(
            mergeMap((response) =>
              this.authStore.userId$.pipe(
                map((userId) => {
                  const filteredInvitationResponse = this.filterPendingValidInvitations(
                    response.data,
                    userId
                  ).filter(
                    (invitation) =>
                      invitation.tokenType === InvitationTokenType.Invited &&
                      !isSendByBuildingManager(invitation, companyId) &&
                      !isSendByLoggedInUser(invitation, userId)
                  )?.length;

                  return filteredInvitationResponse;
                })
              )
            ),
            map((count) => InvitationsActions.getIncomingInvitationsCountSuccess({ count })),
            catchError(() => of(InvitationsActions.getIncomingInvitationsCountError()))
          )
      )
    );
  });

  listPendingTransferInstallationInvitations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.listPendingTransferInstallationInvitations),
      switchMap((action) =>
        this.invitationsApi
          .listInvitations({
            limit: 100,
            installationId: action.installationId,
            status: InvitationStatusType.Pending,
          })
          .pipe(
            map((response: ListResponse<Invitation>) => {
              return InvitationsActions.listPendingTransferInstallationInvitationsSuccess({
                pendingInvitations: response.data,
              });
            }),
            catchError(() =>
              of(InvitationsActions.listPendingTransferInstallationInvitationsError())
            )
          )
      )
    );
  });

  listInstallationInvitations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.listInstallationInvitations),
      debounceTime(InvitationsEffects.RELOAD_INVITATIONS_DEBOUNCE),
      switchMap((action) =>
        of(
          InvitationsActions.bulkListInvitations({
            installationId: action.installationId,
          })
        )
      )
    );
  });

  bulkUpdateBuildingManagerInviations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.bulkSaveBuildingMangerInvitations),
      concatLatestFrom(() => this.installationDetailsStore.getInstallationId$),
      switchMap(([action, installationId]) => {
        const invitationsToCreatePresent = action.invitationsToSave.invitationsToCreate.length;
        const invitationsToUpdatePresent = action.invitationsToSave.invitationsToUpdate.length;
        if (invitationsToCreatePresent && !invitationsToUpdatePresent) {
          return of(
            InvitationsActions.bulkCreateInstallationInvitations({
              installationId,
              invitationsToCreate: action.invitationsToSave.invitationsToCreate,
            })
          );
        } else {
          return of(
            InvitationsActions.bulkUpdateInstallationInvitations({
              installationId,
              invitationsToUpdate: action.invitationsToSave.invitationsToUpdate,
              invitationsToCreate: action.invitationsToSave.invitationsToCreate,
            })
          );
        }
      })
    );
  });

  bulkCreateInstallationInvitations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.bulkCreateInstallationInvitations),
      mergeMap((action) => {
        const requests = flatMap(
          action.invitationsToCreate.map((entry) =>
            this.invitationsApi.createInvitation(entry).pipe(
              tap(() => this.invitationsMessagesService.onSendSuccess()),
              catchError(() => {
                this.toastService.error({
                  message: this.translateService.instant('+INVITATIONS.CREATE_INVITATION_ERROR'),
                });

                return of(null);
              })
            )
          )
        );

        return forkJoin(requests).pipe(
          map((createdInvitaions) => {
            this.uiService.unblockUI();
            createdInvitaions.forEach((entry) => {
              this.store.dispatch(
                InvitationsActions.createInvitationSuccess({ response: entry.data })
              );
            });

            return InvitationsActions.bulkSaveBuildingMangerInvitationsSuccess();
          }),
          catchError((error: { errorType?: string; message?: string; statusCode?: number }) => {
            this.uiService.unblockUI();
            this.toastService.error({
              message: this.translateService.instant('+INVITATIONS.CREATE_INVITATION_ERROR'),
            });
            this.invitationsStore.listInstallationInvitations(action.installationId);

            return of(InvitationsActions.createInvitationError({ error }));
          })
        );
      })
    );
  });

  bulkUpdateInstalltionInvitations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.bulkUpdateInstallationInvitations),
      mergeMap((action) => {
        const requests = flatMap(
          action.invitationsToUpdate.map((entry) =>
            this.invitationsApi.updateInvitation(entry.token, { status: entry.status }).pipe(
              map((res) => res),
              catchError(() => of(null))
            )
          )
        );

        return forkJoin(requests).pipe(
          map((updatedInvitations: GetResponse<Invitation>[]) => {
            this.uiService.unblockUI();
            updatedInvitations.forEach((entry) => {
              this.store.dispatch(
                InvitationsActions.updateInvitationSuccess({
                  response: entry,
                  ownerName: undefined,
                })
              );
            });

            if (action.invitationsToCreate.length) {
              return InvitationsActions.bulkCreateInstallationInvitations({
                installationId: action.installationId,
                invitationsToCreate: action.invitationsToCreate,
              });
            } else {
              return InvitationsActions.bulkSaveBuildingMangerInvitationsSuccess();
            }
          }),
          catchError(() => {
            this.uiService.unblockUI();
            this.invitationsStore.listInstallationInvitations(action.installationId);

            return of(InvitationsActions.updateInvitationError());
          })
        );
      })
    );
  });

  bulkSaveBuildingMangerInvitationsSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.bulkSaveBuildingMangerInvitationsSuccess),
      concatLatestFrom(() => this.installationDetailsStore.getInstallationId$),
      switchMap(([_action, installationId]) => {
        this.invitationsStore.listInstallationInvitations(installationId);

        return of(InvitationsActions.bulkSaveBuildingMangerInvitationsComplete());
      })
    );
  });

  bulkListInvitations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.bulkListInvitations),
      concatLatestFrom(() => [
        this.authStore.getCompanyId(),
        this.authStore.isUserWithRoleInvitation$,
      ]),
      switchMap(([action, companyId, isCommercialUser]) => {
        let queryParams = { limit: 100 };

        if (action.installationId) {
          queryParams['installationId'] = action.installationId;
        }

        return forkJoin([
          this.invitationsApi
            .listInvitations({ ...queryParams, status: InvitationStatusType.Pending })
            .pipe(
              map((res) => {
                const output = { ...res };

                return output;
              }),
              catchError(() => of(null))
            ),
          this.invitationsApi
            .listInvitations({ ...queryParams, status: InvitationStatusType.Expired })
            .pipe(catchError(() => of(null))),
          this.invitationsApi
            .listInvitations({ ...queryParams, status: InvitationStatusType.Rejected })
            .pipe(catchError(() => of(null))),
          this.invitationsApi
            .listInvitations({ ...queryParams, status: InvitationStatusType.Accepted })
            .pipe(catchError(() => of(null))),
          this.invitationsApi
            .listInvitations({ ...queryParams, status: InvitationStatusType.Canceled })
            .pipe(catchError(() => of(null))),
          this.invitationsApi
            .listInvitations({ ...queryParams, status: InvitationStatusType.Terminated })
            .pipe(catchError(() => of(null))),
        ]).pipe(
          // User id is required for proper invitations filter, don't ask for it along with api calls
          concatLatestFrom(() => this.authStore.userId$),
          map(
            ([
              [
                pendingInvitations,
                expiredInvitations,
                rejectedInvitations,
                acceptedInvitations,
                canceledInvitations,
                terminatedInvitations,
              ],
              userId,
            ]) => {
              const combinedInvitations: Invitation[] = [
                ...pendingInvitations.data,
                ...expiredInvitations.data,
                ...rejectedInvitations.data,
                ...acceptedInvitations.data,
                ...canceledInvitations.data,
                ...terminatedInvitations.data,
              ];
              const buildingManagerInvitations = this.getBuildingManagerInvitations(
                combinedInvitations
              );

              const combinedResponses: BulkListInvitationsCombinedResponses = {
                pendingInvitations,
                expiredInvitations,
                rejectedInvitations,
                acceptedInvitations,
                canceledInvitations,
                terminatedInvitations,
                userId,
                buildingManagerInvitations,
              };
              combinedResponses.pendingInvitations.data = this.filterPendingValidInvitations(
                combinedResponses.pendingInvitations.data,
                userId
              );

              return combinedResponses;
            }
          ),
          tap((response) => {
            if (!isCommercialUser) {
              response.pendingInvitations.data.forEach((invitation) => {
                this.store.dispatch(
                  InvitationsActions.getInvitationOwnerDetails({ token: invitation.token })
                );
              });
            }
          }),
          tap((response) => {
            // Request an installation details for each `Pending` invitation
            response.pendingInvitations.data
              .filter((invitation) => invitation.status === InvitationStatusType.Pending)
              .forEach((invitation) => {
                this.store.dispatch(
                  InvitationsActions.getInvitationInstallationDetails({ token: invitation.token })
                );
              });
          }),
          map((response) =>
            InvitationsActions.bulkListInvitationsSuccess({
              invitations: {
                pending: response.pendingInvitations,
                expired: response.expiredInvitations,
                declined: response.rejectedInvitations,
                accepted: response.acceptedInvitations,
                canceled: response.canceledInvitations,
                terminated: response.terminatedInvitations,
                companyId,
                userId: response.userId,
              },
              buildingManagerInvitations: response.buildingManagerInvitations,
            })
          ),
          catchError(() => {
            this.toastService.error({
              message: this.translateService.instant('+INVITATIONS.CREATE_INVITATION_ERROR'),
            });

            return of(InvitationsActions.bulkListInvitationsError());
          })
        );
      })
    );
  });

  updateInvitations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.updateInvitation),
      tap(() => this.uiService.blockUI()),
      mergeMap((action) =>
        this.invitationsApi.updateInvitation(action.token, action.request).pipe(
          tap(() => this.uiService.unblockUI()),
          map((response) =>
            InvitationsActions.updateInvitationSuccess({
              response,
              ownerName: action.ownerName,
            })
          ),
          catchError(() => {
            this.uiService.unblockUI();

            return of(InvitationsActions.updateInvitationError());
          })
        )
      )
    );
  });

  notifyInvitationAccepted$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InvitationsActions.updateInvitationSuccess),
        tap((action) => {
          if (action.response.data.status === InvitationStatusType.Accepted) {
            this.invitationsService.emitInvitationAccepted(action.response.data.token);
          }
        })
      );
    },
    { dispatch: false }
  );

  updateCommercialInvitations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.updateCommercialInvitation.attempt),
      tap(() => this.uiService.blockUI()),
      mergeMap((action) =>
        this.invitationsApi.updateInvitation(action.token, action.request).pipe(
          tap(() => this.uiService.unblockUI()),
          map(() =>
            InvitationsActions.updateCommercialInvitation.success({
              token: action.token,
              status: action.request.status,
            })
          ),
          catchError((error) => {
            this.uiService.unblockUI();
            const message = this.getTranslationForErrorMessage(error);
            if (message) {
              this.toastService.error({
                message,
              });
            }

            return of(InvitationsActions.updateCommercialInvitation.failed());
          })
        )
      )
    );
  });

  fetchInstallationsAfterAcceptingCommercialInvitation$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InvitationsActions.updateCommercialInvitation.success),
        tap(() => {
          this.installationsStore.listInstallations();
        })
      );
    },
    { dispatch: false }
  );

  updateInvitationsSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.updateInvitationSuccess),
      map((action) => {
        const status = action?.response?.data?.status;
        if (status === InvitationStatusType.Accepted) {
          this.installationsStore.listInstallations();
          this.invitationsMessagesService.onAcceptSuccess(action.ownerName);

          return InvitationsActions.acceptIncomingInvitation({
            invitation: action.response.data,
          });
        } else if (status === InvitationStatusType.Rejected) {
          this.invitationsMessagesService.onRejectSuccess(action.ownerName);

          return InvitationsActions.rejectIncomingInvitation({
            invitation: action.response.data,
          });
        } else if (status === InvitationStatusType.Canceled) {
          this.invitationsMessagesService.onCancelSuccess();

          return InvitationsActions.cancelInvitation({
            invitation: action.response.data,
          });
        }

        return InvitationsActions.cancelInvitation({
          invitation: action?.response?.data || ({} as Invitation),
        });
      })
    );
  });

  getInvitationOwnerDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.getInvitationOwnerDetails),
      mergeMap((action) =>
        this.invitationsApi.getInvitationOwnerDetails(action.token).pipe(
          map((response) =>
            InvitationsActions.getInvitationOwnerDetailsSuccess({
              token: action.token,
              response,
            })
          ),
          catchError(() => of(InvitationsActions.getInvitationOwnerDetailsError()))
        )
      )
    );
  });

  getCommercialInvitationDetailsPreparation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(ofType(InvitationsActions.bulkListInvitationsSuccess)).pipe(
      concatLatestFrom(() => [
        this.authStore.isUserWithRoleInvitation$,
        this.invitationsStore.incomingInvitations$,
      ]),
      filter(([_action, isCommercialUser, _invitations]) => !!isCommercialUser),
      switchMap(([_action, _isCommercialUser, invitations]) => {
        const getCommercialInvitationActions = invitations.map((invitation) =>
          InvitationsActions.getCommercialInvitationDetails.attempt({
            token: invitation.token,
            installationId: invitation.installationId,
            note: invitation?.comment,
          })
        );

        return getCommercialInvitationActions;
      })
    );
  });

  getCommercialInvitationDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.getCommercialInvitationDetails.attempt),
      mergeMap((action) => {
        const ownerDetails$ = this.invitationsApi
          .getInvitationOwnerDetails(action.token)
          .pipe(map((response) => response.data));

        const materialNames$ = this.invitationsApi
          .getInvitationInstallationDetails(action.token)
          .pipe(map((response) => response.data));

        return forkJoin([ownerDetails$, materialNames$]).pipe(
          map(([owner, installation]) => {
            const { firstName, lastName, phoneNumber, mobileNumber } = owner;
            const { street, houseNumber, zip, city } = installation.address;
            const commercialInvitation: CommercialInvitation = {
              firstName,
              lastName,
              street,
              houseNumber,
              zip,
              city,
              phoneNumber: phoneNumber ?? mobileNumber,
              note: action.note,
              status: null,
            };

            return InvitationsActions.getCommercialInvitationDetails.success({
              token: action.token,
              data: commercialInvitation,
            });
          }),
          catchError(() => of(InvitationsActions.getCommercialInvitationDetails.failed()))
        );
      })
    );
  });

  getInvitationInstallationDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.getInvitationInstallationDetails),
      mergeMap((action) =>
        this.invitationsApi.getInvitationInstallationDetails(action.token).pipe(
          map((response) =>
            InvitationsActions.getInvitationInstallationDetailsSuccess({
              token: action.token,
              response,
            })
          ),
          catchError(() => of(InvitationsActions.getInvitationInstallationDetailsError()))
        )
      )
    );
  });

  readUserInvitations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InvitationsActions.getUserInvitations),
      switchMap((action) =>
        this.invitationsApi.listInvitations(action.params).pipe(
          map((response) => InvitationsActions.getUserInvitationsSuccess(response)),
          catchError(() => of(InvitationsActions.getUserInvitationsError()))
        )
      )
    );
  });

  constructor(
    private invitationsApi: InvitationsApi,
    private actions$: Actions,
    private store: Store,
    private uiService: UIService,
    private invitationsMessagesService: InvitationsMessagesService,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private installationsStore: InstallationsStore,
    private invitationsStore: InvitationsStore,
    private installationDetailsStore: InstallationDetailsStore,
    private authStore: AuthStore,
    private invitationsService: InvitationsService
  ) {}

  private filterPendingValidInvitations(
    pendingInvitation: Invitation[],
    userId: string
  ): Invitation[] {
    return pendingInvitation.filter((invitation) => {
      const invitationToMyself = invitation.inviterAccount === userId;
      const invitationWithoutCustomerNumber = !invitation.customerNumber;
      const accessLevelOwner =
        invitation.accessLevel === InvitationAccessLevel.Owner ||
        invitation.accessLevel === InvitationAccessLevel.CommercialOwner;
      const isGatewayCommissioningInvitation =
        invitationToMyself && invitationWithoutCustomerNumber && accessLevelOwner;

      return (
        invitation.status === InvitationStatusType.Pending && !isGatewayCommissioningInvitation
      );
    });
  }

  private getTranslationForErrorMessage(error: ViError): string {
    let translation = '';
    switch (error?.message) {
      // 400
      case 'COMPANY_HAS_NO_EMPLOYEES':
        translation = '+INVITATIONS.INVITATIONS.ERROR.COMPANY_HAS_NO_EMPLOYEES';
        break;
      case 'COMPANY_HAS_NO_EMAIL':
        translation = '+INVITATIONS.INVITATIONS.ERROR.COMPANY_HAS_NO_EMAIL';
        break;
      case 'COMPANY_NOT_FOUND':
        translation = '+INVITATIONS.INVITATIONS.ERROR.COMPANY_NOT_FOUND';
        break;
      case 'NOT_ALLOWED':
        translation = '+INVITATIONS.INVITATIONS.ERROR.NOT_ALLOWED';
        break;
      case 'TOKEN_EXPIRED':
        translation = '+INVITATIONS.INVITATIONS.ERROR.TOKEN_EXPIRED';
        break;
      case 'ACCESS_ALREADY_GRANTED':
        translation = '+INVITATIONS.INVITATIONS.ERROR.ACCESS_ALREADY_GRANTED';
        break;
      case 'VALIDATION_ERROR':
        translation = '+INVITATIONS.INVITATIONS.ERROR.VALIDATION_ERROR';
        break;
      // 401, 403
      case 'UNAUTHORIZED':
        translation = '+INVITATIONS.INVITATIONS.ERROR.UNAUTHORIZED';
        break;
      // 402
      case 'PACKAGE_NOT_PAID_FOR':
        translation = '+INVITATIONS.INVITATIONS.ERROR.PACKAGE_NOT_PAID_FOR';
        break;
      // 404
      case 'NOT_FOUND':
        translation = '+INVITATIONS.INVITATIONS.ERROR.NOT_FOUND';
        break;
      case 'ENDPOINT_NOT_FOUND':
        translation = '+INVITATIONS.INVITATIONS.ERROR.ENDPOINT_NOT_FOUND';
        break;
      default:
        translation = '+INVITATIONS.INVITATIONS.ERROR.REQUEST_IS_INVALID';
        break;
    }

    return this.translateService.instant(translation);
  }

  getBuildingManagerInvitations(invitations: Invitation[]): Invitation[] {
    const buildingManagerInvitations = invitations.filter(
      (entry) =>
        entry.accessLevel === InvitationAccessLevel.ServiceContractor ||
        entry.accessLevel === InvitationAccessLevel.Operator
    );

    return buildingManagerInvitations.length ? buildingManagerInvitations : [];
  }
}
